import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { TopBar } from '@/components/layout/TopBar';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { format } from 'date-fns';
import { Search, ExternalLink } from 'lucide-react';

interface BugReport {
  id: string;
  created_at: string;
  summary: string;
  shop_name: string;
  user_email: string;
  severity: string;
  status: string;
  environment: string;
}

export default function BugReports() {
  const { membership, activeShop } = useAuth();
  const navigate = useNavigate();
  const [reports, setReports] = useState<BugReport[]>([]);
  const [filteredReports, setFilteredReports] = useState<BugReport[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [envFilter, setEnvFilter] = useState('all');

  // Check if user is manager
  useEffect(() => {
    if (membership && membership.role !== 'MANAGER') {
      navigate('/');
    }
  }, [membership, navigate]);

  // Fetch bug reports
  useEffect(() => {
    const fetchReports = async () => {
      if (!activeShop) return;

      setIsLoading(true);
      try {
        // R125-M-1: narrow select — list view renders id/created_at/
        // summary/shop_name/user_email/severity/status/environment and
        // filters on summary/shop_name/status/severity/environment. Drops
        // trying_to_do, what_happened, route_path, browser_info,
        // context_json (may contain PII snapshots) from the listing cache.
        const { data, error } = await supabase
          .from('bug_reports')
          .select('id, created_at, summary, shop_name, user_email, severity, status, environment, plan_tier, tenant_id')
          .order('created_at', { ascending: false });

        if (error) throw error;
        setReports(data || []);
        setFilteredReports(data || []);
      } catch (error) {
        console.error('Error fetching bug reports:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchReports();
  }, [activeShop]);

  // Apply filters
  useEffect(() => {
    let filtered = reports;

    if (searchTerm) {
      filtered = filtered.filter(
        (r) =>
          r.summary.toLowerCase().includes(searchTerm.toLowerCase()) ||
          r.shop_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter((r) => r.status === statusFilter);
    }

    if (severityFilter !== 'all') {
      filtered = filtered.filter((r) => r.severity === severityFilter);
    }

    if (envFilter !== 'all') {
      filtered = filtered.filter((r) => r.environment === envFilter);
    }

    setFilteredReports(filtered);
  }, [reports, searchTerm, statusFilter, severityFilter, envFilter]);

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'Blocks me from using the app':
        return 'destructive';
      case 'Major problem but I can work around it':
        return 'default';
      case 'Minor issue / annoyance':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open':
        return 'destructive';
      case 'in_review':
        return 'default';
      case 'fixed':
        return 'secondary';
      case 'closed':
        return 'outline';
      default:
        return 'outline';
    }
  };

  if (membership?.role !== 'MANAGER') {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      <TopBar />
      <main className="container mx-auto px-4 py-8 max-w-7xl">
        <div className="mb-6">
          <h1 className="text-3xl font-bold mb-2">Bug Reports</h1>
          <p className="text-muted-foreground">
            Review and manage issue reports from alpha and beta testers
          </p>
        </div>

        <div className="bg-card rounded-lg border p-6 space-y-4">
          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search summary or shop..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>

            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="in_review">In Review</SelectItem>
                <SelectItem value="fixed">Fixed</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>

            <Select value={severityFilter} onValueChange={setSeverityFilter}>
              <SelectTrigger>
                <SelectValue placeholder="All severities" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All severities</SelectItem>
                <SelectItem value="Blocks me from using the app">Blocking</SelectItem>
                <SelectItem value="Major problem but I can work around it">Major</SelectItem>
                <SelectItem value="Minor issue / annoyance">Minor</SelectItem>
                <SelectItem value="Just feedback / suggestion">Feedback</SelectItem>
              </SelectContent>
            </Select>

            <Select value={envFilter} onValueChange={setEnvFilter}>
              <SelectTrigger>
                <SelectValue placeholder="All environments" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All environments</SelectItem>
                <SelectItem value="production">Production</SelectItem>
                <SelectItem value="staging">Staging</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Table */}
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Summary</TableHead>
                  <TableHead>Shop</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Severity</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Env</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : filteredReports.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center">
                      No bug reports found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredReports.map((report) => (
                    <TableRow
                      key={report.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => navigate(`/bug-reports/${report.id}`)}
                    >
                      <TableCell className="font-mono text-sm">
                        {format(new Date(report.created_at), 'MMM d, yyyy')}
                      </TableCell>
                      <TableCell className="font-medium">{report.summary}</TableCell>
                      <TableCell>{report.shop_name}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {report.user_email}
                      </TableCell>
                      <TableCell>
                        <Badge variant={getSeverityColor(report.severity)}>
                          {report.severity.split(' ')[0]}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={getStatusColor(report.status)}>
                          {report.status.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {report.environment}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/bug-reports/${report.id}`);
                          }}
                        >
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          <div className="text-sm text-muted-foreground">
            Showing {filteredReports.length} of {reports.length} reports
          </div>
        </div>
      </main>
    </div>
  );
}
